'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import {
  BRAND_CORE_EXPLANATION,
  BRAND_CORE_MESSAGE,
  BRAND_TRUST_POINTS,
} from '@/content/brand';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 3);
    }, 2200);

    return () => clearInterval(interval);
  }, [isHydrated]);

  return (
    <section
      className={`relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#f5fbf6] to-background ${className}`}
    >
      <div className="absolute inset-0 opacity-50">
        <div className="absolute -top-24 -left-20 w-72 h-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute top-16 right-0 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full border border-accent/20">
              <Icon name="ShieldCheckIcon" size={18} className="text-accent" />
              <span className="text-sm font-semibold">{BRAND_CORE_MESSAGE}</span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-heading font-extrabold text-primary leading-tight">
              Connect without sharing your phone number
            </h1>

            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              {BRAND_CORE_EXPLANATION}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/get-started"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-1 transition-all duration-300"
              >
                Get ParkPing
                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
              </Link>

              <Link
                href="/how-it-works"
                className="inline-flex items-center justify-center px-8 py-4 bg-white border border-border text-primary font-heading font-semibold rounded-xl hover:bg-muted transition-all duration-300"
              >
                See How It Works
                <Icon name="PlayIcon" size={20} className="ml-2" />
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-2">
              {BRAND_TRUST_POINTS.map((point) => (
                <div key={point} className="flex items-center gap-2">
                  <Icon name="CheckCircleIcon" size={18} className="text-accent" />
                  <span className="text-sm text-muted-foreground">{point}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-border bg-white shadow-card">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                      <Icon name="PhoneIcon" size={20} className="text-accent" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-primary">Secure call relay</div>
                      <div className="text-xs text-muted-foreground">
                        Toll-free | Code-based | Private
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">Live</div>
                </div>

                <div className="mt-8 grid gap-4">
                  <div
                    className={`rounded-2xl border p-4 transition-all duration-500 ${
                      step === 0
                        ? 'border-accent/40 bg-accent/10 shadow-[0_0_0_1px_rgba(66,175,71,0.15)]'
                        : 'border-border bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary">
                          1
                        </span>
                        <span className="text-sm font-semibold text-primary">Caller dials toll-free</span>
                      </div>
                      <Icon name="SignalIcon" size={18} className={step === 0 ? 'text-accent' : 'text-muted-foreground'} />
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Personal number stays private.
                    </div>
                  </div>

                  <div
                    className={`rounded-2xl border p-4 transition-all duration-500 ${
                      step === 1
                        ? 'border-accent/40 bg-accent/10 shadow-[0_0_0_1px_rgba(66,175,71,0.15)]'
                        : 'border-border bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary">
                          2
                        </span>
                        <span className="text-sm font-semibold text-primary">Enters 4-digit code</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <span
                            key={i}
                            className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
                              step === 1 ? 'bg-accent' : 'bg-border'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Code matches the card on dashboard.
                    </div>
                  </div>

                  <div
                    className={`rounded-2xl border p-4 transition-all duration-500 ${
                      step === 2
                        ? 'border-accent/40 bg-accent/10 shadow-[0_0_0_1px_rgba(66,175,71,0.15)]'
                        : 'border-border bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary">
                          3
                        </span>
                        <span className="text-sm font-semibold text-primary">Gets connected securely</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex h-2.5 w-2.5 rounded-full ${
                            step === 2 ? 'bg-accent' : 'bg-border'
                          }`}
                        />
                        <span className="text-xs text-muted-foreground">Connected</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      Owner receives the call without public number sharing.
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted border border-border px-3 py-1 text-xs text-primary">
                    <Icon name="ShieldCheckIcon" size={14} className="text-accent" />
                    Privacy protected
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted border border-border px-3 py-1 text-xs text-primary">
                    <Icon name="ClockIcon" size={14} className="text-accent" />
                    Fast resolution
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted border border-border px-3 py-1 text-xs text-primary">
                    <Icon name="ExclamationTriangleIcon" size={14} className="text-accent" />
                    Prevents conflict
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
